import { Dumbbell } from 'lucide-react';
import React from 'react';
import Map, { Source, Layer, Marker, FullscreenControl } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Card, CardContent, CardHeader, CardTitle } from '@components/ui/card';
import { cn } from '@core/utils/cn';
import { MAPBOX_ACCESS_TOKEN } from '../../../../core/map';
import { calculateInitialViewState } from '../utils/GpxMap.utils';
import { useGpxMap } from '../utils/GpxMap.hook';
import { ActivityOverviewSupportDto } from '@api/FitnessActivityClient';
import { GpxMapLegend } from '../utils/GpxMapLegend';

interface GpxMapProps {
    data: ActivityOverviewSupportDto | null;
    isOnlyDay?: boolean;
}

export function GpxMap({ data, isOnlyDay = false }: GpxMapProps) {
    const { parsedWorkouts, filteredCheckIns } = useGpxMap({
        data: data || null
    });

    const initialViewState = calculateInitialViewState(
        parsedWorkouts,
        filteredCheckIns
    );

    if (parsedWorkouts.length === 0 && filteredCheckIns.length === 0) {
        return null;
    }

    return (
        <Card>
            <CardHeader>
                <CardTitle>Activity Map</CardTitle>
            </CardHeader>
            <CardContent>
                <div
                    className={cn(
                        'w-full overflow-hidden rounded-md',
                        isOnlyDay ? 'h-[400px]' : 'aspect-video'
                    )}
                >
                    <Map
                        initialViewState={initialViewState}
                        style={{ width: '100%', height: '100%' }}
                        mapStyle="mapbox://styles/mapbox/dark-v11"
                        mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
                    >
                        {parsedWorkouts.map((workout) => (
                            <Source
                                key={workout.id}
                                id={`source-${workout.id}`}
                                type="geojson"
                                data={{
                                    type: 'Feature',
                                    properties: {},
                                    geometry: {
                                        type: 'LineString',
                                        coordinates: workout.positions
                                    }
                                }}
                            >
                                <Layer
                                    id={`layer-${workout.id}`}
                                    type="line"
                                    layout={{
                                        'line-join': 'round',
                                        'line-cap': 'round'
                                    }}
                                    paint={{
                                        'line-color': workout.color,
                                        'line-width': 4
                                    }}
                                />
                            </Source>
                        ))}

                        {filteredCheckIns.map((checkIn) => (
                            <Marker
                                key={checkIn.id}
                                longitude={checkIn.location[0]}
                                latitude={checkIn.location[1]}
                                anchor="bottom"
                            >
                                <div
                                    className="flex flex-col items-center"
                                    style={{ color: checkIn.color }}
                                >
                                    <Dumbbell className="size-6" />
                                    <span className="rounded-sm bg-black/70 px-1 text-xs text-white">
                                        {checkIn.name}
                                    </span>
                                </div>
                            </Marker>
                        ))}
                        <FullscreenControl position="top-left" />
                        <GpxMapLegend
                            filteredCheckIns={filteredCheckIns}
                            parsedWorkouts={parsedWorkouts}
                        />
                    </Map>
                </div>
            </CardContent>
        </Card>
    );
}
